import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { filmCategoryMeta, newsCategoryMeta } from "@/mocks/categories";
import type { FilmCategory, NewsCategory } from "@/types";
import { useFavorites } from "@/hooks/useFavorites";
import SearchOverlay from "./SearchOverlay";

const filmTypes: FilmCategory[] = ["movie", "tv", "variety"];
const newsTypes: NewsCategory[] = ["industry", "company", "release", "interview"];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { favorites } = useFavorites();
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileGroup, setMobileGroup] = useState<string | null>(null);

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen || searchOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen, searchOpen]);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `relative px-3 py-2 text-sm font-medium transition-colors ${
      transparent
        ? isActive
          ? "text-accent-300"
          : "text-background-50 hover:text-accent-300"
        : isActive
        ? "text-accent-600"
        : "text-foreground-700 hover:text-accent-600"
    }`;

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          transparent
            ? "bg-gradient-to-b from-foreground-950/70 to-transparent"
            : "border-b border-background-200 bg-background-50/95 backdrop-blur-md"
        }`}
      >
        <div
          className={`flex h-16 items-center justify-between gap-4 px-4 md:h-20 md:px-6 lg:px-10`}
        >
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-500 text-background-50 md:h-10 md:w-10">
              <i className="ri-movie-2-fill text-lg md:text-xl" />
            </span>
            <span className="flex flex-col leading-none">
              <span
                className={`font-heading text-lg font-bold tracking-tight md:text-xl ${
                  transparent ? "text-background-50" : "text-foreground-950"
                }`}
              >
                星光传媒
              </span>
              <span
                className={`mt-0.5 text-[10px] tracking-[0.2em] ${
                  transparent ? "text-background-200" : "text-foreground-500"
                }`}
              >
                STARLIGHT MEDIA
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-1 lg:flex">
            <NavLink to="/" className={linkClass} end>
              首页
            </NavLink>

            <div className="group relative">
              <button
                type="button"
                className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors ${
                  transparent
                    ? "text-background-50 hover:text-accent-300"
                    : "text-foreground-700 hover:text-accent-600"
                }`}
              >
                影视分类
                <i className="ri-arrow-down-s-line text-base" />
              </button>
              <div className="invisible absolute left-0 top-full w-56 translate-y-2 rounded-lg border border-background-200 bg-background-50 p-2 opacity-0 shadow-sm transition-all group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                {filmTypes.map((type) => (
                  <Link
                    key={type}
                    to={`/category/${type}`}
                    className="flex items-center justify-between rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-accent-600"
                  >
                    {filmCategoryMeta[type].label}
                    <i className="ri-arrow-right-s-line text-foreground-400" />
                  </Link>
                ))}
              </div>
            </div>

            <div className="group relative">
              <button
                type="button"
                className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors ${
                  transparent
                    ? "text-background-50 hover:text-accent-300"
                    : "text-foreground-700 hover:text-accent-600"
                }`}
              >
                资讯分类
                <i className="ri-arrow-down-s-line text-base" />
              </button>
              <div className="invisible absolute left-0 top-full w-56 translate-y-2 rounded-lg border border-background-200 bg-background-50 p-2 opacity-0 shadow-sm transition-all group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                {newsTypes.map((type) => (
                  <Link
                    key={type}
                    to={`/news/${type}`}
                    className="flex items-center justify-between rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-accent-600"
                  >
                    {newsCategoryMeta[type].label}
                    <i className="ri-arrow-right-s-line text-foreground-400" />
                  </Link>
                ))}
              </div>
            </div>

            <NavLink to="/about" className={linkClass}>
              关于我们
            </NavLink>
            <NavLink to="/contact" className={linkClass}>
              联系我们
            </NavLink>
          </nav>

          {/* Desktop actions */}
          <div className="hidden items-center gap-2 lg:flex">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="搜索影视作品"
              className={`flex h-9 w-9 items-center justify-center rounded-md transition-colors ${
                transparent
                  ? "text-background-50 hover:bg-background-50/15"
                  : "text-foreground-700 hover:bg-background-100"
              }`}
            >
              <i className="ri-search-line text-lg" />
            </button>
            <Link
              to="/favorites"
              aria-label="我的收藏"
              className={`relative flex h-9 w-9 items-center justify-center rounded-md transition-colors ${
                transparent
                  ? "text-background-50 hover:bg-background-50/15"
                  : "text-foreground-700 hover:bg-background-100"
              }`}
            >
              <i className="ri-heart-line text-lg" />
              {favorites.length > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] font-semibold text-background-50">
                  {favorites.length}
                </span>
              )}
            </Link>
            <button
              type="button"
              onClick={() => navigate("/contact")}
              className="ml-1 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              合作咨询
            </button>
          </div>

          {/* Mobile actions */}
          <div className="flex items-center gap-1 lg:hidden">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="搜索"
              className={`flex h-9 w-9 items-center justify-center rounded-md ${
                transparent ? "text-background-50" : "text-foreground-700"
              }`}
            >
              <i className="ri-search-line text-lg" />
            </button>
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="打开菜单"
              className={`flex h-9 w-9 items-center justify-center rounded-md ${
                transparent ? "text-background-50" : "text-foreground-800"
              }`}
            >
              <i className="ri-menu-line text-xl" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-[60] lg:hidden ${
          drawerOpen ? "pointer-events-auto" : "pointer-events-none"
        }`}
      >
        <div
          className={`absolute inset-0 bg-foreground-950/50 transition-opacity ${
            drawerOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setDrawerOpen(false)}
        />
        <aside
          className={`absolute right-0 top-0 h-full w-[82%] max-w-sm bg-background-50 transition-transform duration-300 ${
            drawerOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex h-16 items-center justify-between border-b border-background-200 px-5">
            <span className="font-heading text-base font-bold text-foreground-950">
              星光传媒
            </span>
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              aria-label="关闭菜单"
              className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 hover:bg-background-100"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
          <nav className="flex flex-col gap-1 overflow-y-auto px-3 py-4">
            <Link
              to="/"
              className="rounded-md px-3 py-3 text-base font-medium text-foreground-800 hover:bg-background-100"
            >
              首页
            </Link>

            <button
              type="button"
              onClick={() => setMobileGroup(mobileGroup === "film" ? null : "film")}
              className="flex items-center justify-between rounded-md px-3 py-3 text-base font-medium text-foreground-800 hover:bg-background-100"
            >
              影视分类
              <i
                className={`ri-arrow-down-s-line text-lg transition-transform ${
                  mobileGroup === "film" ? "rotate-180" : ""
                }`}
              />
            </button>
            {mobileGroup === "film" && (
              <div className="mb-1 ml-3 flex flex-col gap-1 border-l border-background-200 pl-3">
                {filmTypes.map((type) => (
                  <Link
                    key={type}
                    to={`/category/${type}`}
                    className="rounded-md px-3 py-2.5 text-sm text-foreground-600 hover:bg-background-100"
                  >
                    {filmCategoryMeta[type].label}
                  </Link>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={() => setMobileGroup(mobileGroup === "news" ? null : "news")}
              className="flex items-center justify-between rounded-md px-3 py-3 text-base font-medium text-foreground-800 hover:bg-background-100"
            >
              资讯分类
              <i
                className={`ri-arrow-down-s-line text-lg transition-transform ${
                  mobileGroup === "news" ? "rotate-180" : ""
                }`}
              />
            </button>
            {mobileGroup === "news" && (
              <div className="mb-1 ml-3 flex flex-col gap-1 border-l border-background-200 pl-3">
                {newsTypes.map((type) => (
                  <Link
                    key={type}
                    to={`/news/${type}`}
                    className="rounded-md px-3 py-2.5 text-sm text-foreground-600 hover:bg-background-100"
                  >
                    {newsCategoryMeta[type].label}
                  </Link>
                ))}
              </div>
            )}

            <Link
              to="/about"
              className="rounded-md px-3 py-3 text-base font-medium text-foreground-800 hover:bg-background-100"
            >
              关于我们
            </Link>
            <Link
              to="/contact"
              className="rounded-md px-3 py-3 text-base font-medium text-foreground-800 hover:bg-background-100"
            >
              联系我们
            </Link>
            <Link
              to="/favorites"
              className="flex items-center justify-between rounded-md px-3 py-3 text-base font-medium text-foreground-800 hover:bg-background-100"
            >
              我的收藏
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-secondary-100 px-1.5 text-xs text-secondary-700">
                {favorites.length}
              </span>
            </Link>
            <Link
              to="/contact"
              className="mt-2 rounded-md bg-accent-500 px-3 py-3 text-center text-base font-semibold text-background-50"
            >
              合作咨询
            </Link>
          </nav>
        </aside>
      </div>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}